import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';

import { catchError, map, of } from 'rxjs';

import { STORAGE_KEYS } from '../constants/storage-keys';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

/**
 * Guard for Donor Portal routes.
 * Requires a valid JWT with role = 'donor'.
 * Handles proactive token refresh if the access token is expired but a refresh token exists.
 */
export const authGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const toast = inject(ToastService);

  if (authService.isAuthenticated()) {
    return checkUserOrigin(authService, toast, router);
  }

  // Token expired or missing. Try to refresh if we have a refresh token.
  const refreshToken = authService.getRefreshToken();
  if (refreshToken) {
    return authService.refreshAndStore().pipe(
      map(() => checkUserOrigin(authService, toast, router)),
      catchError(() => {
        return of(redirectToLogin(router, state.url));
      })
    );
  }

  return redirectToLogin(router, state.url);
};

/** Shared check for user origin/role after successful auth. */
function checkUserOrigin(authService: AuthService, toast: ToastService, router: Router): boolean {
  const userInfo = authService.getUserInfo();
  if (!userInfo || userInfo.userOrigin !== 'External') {
    toast.error('Access Denied', 'You do not have permission to access the Donor Portal.');
    router.navigate(['/login']);
    return false;
  }
  return true;
}

/** Helper to store return URL and navigate to login. */
function redirectToLogin(router: Router, returnUrl: string): boolean {
  sessionStorage.setItem(STORAGE_KEYS.DONOR_RETURN_URL, returnUrl);
  router.navigate(['/login']);
  return false;
}
